import React, { useEffect, useState } from 'react';
import './air.css';
import { loadYaml } from '../utilis/loadYAML';
import BifurcatedPromo from '../components/BifurcatedPromo';
import VideoComp from '../components/VideoComp';

// ✅ Individual icon imports (type-safe)
import { FaChartLine, FaRobot, FaNetworkWired, FaBolt, FaLock } from 'react-icons/fa';

// ✅ TS-safe icon map with component references (no JSX here)
const iconMap: Record<string, any> = {
  'future': FaChartLine,
  'autonomy': FaRobot,
  'connectivity': FaNetworkWired,
  'bolt': FaBolt,
  'shield': FaLock,
};

// ✅ Safe icon rendering via React.createElement
const getIcon = (iconName: string): React.ReactNode | null => {
  const IconComponent = iconMap[iconName];
  if (!IconComponent) {
    console.warn(`Icon not found for: ${iconName}`);
    return null;
  }
  return React.createElement(IconComponent, { className: 'feature-icon-svg' });
};

// Interfaces for YAML data
interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface Section {
  name: any;
  designation: any;
  type: string;
  id?: string;
  title?: string;
  description?: string;
  heading?: string;
  images?: string[];
  image?: string;
  video?: string;
  features?: Feature[];
  quote?: string;
}

function CyberSecurity() {
  const [sections, setSections] = useState<Section[]>([]);

  useEffect(() => {
    loadYaml('/config/cybersecurity.yaml')
      .then((data) => setSections(data.sections || []))
      .catch((err) => console.error('YAML load error:', err));
  }, []);

  return (
    <div className="mission-page">
      {sections.map((section, index) => {
        const renderHr = index !== 0 && section.type !== 'hero';

        // HERO SECTION
        if (section.type === 'hero' && section.image) {
          return (
            <section
              className="career-banner"
              style={{ backgroundImage: `url(${section.image})` }}
              key={index}
              id={section.id || `section-${index}`}
            >
              {section.title && <h1 className="career-banner-title">{section.title}</h1>}
            </section>
          );
        }

        // TEXT SECTION
        if (section.type === 'text') {
          const [boldLine, ...rest] = (section.description || '').split('---');
          const secondHalf = rest.join('---').trim();

          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <section
                className="mission-section text-section"
                id={section.id || `section-${index}`}
              >
                <h2>{section.title}</h2>
                <div className="text-bifurcated-container">
                  <div className="text-bifurcated-left">
                    {boldLine && <p className="bold-line">{boldLine.trim()}</p>}
                  </div>
                  <div className="text-bifurcated-right">
                    {secondHalf && <p className="secondary-text">{secondHalf}</p>}
                  </div>
                </div>
              </section>
            </React.Fragment>
          );
        }

        // VIDEO PROMO SECTION
        if (section.type === 'videoPromo' && section.video) {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <VideoComp
                id={section.id || `video-${index}`}
                video={section.video}
                title={section.title || ''}
                heading={section.heading || ''}
                description={section.description || ''}
              />
            </React.Fragment>
          );
        }

        // BIFURCATED PROMO SECTION
        if (section.type === 'bifurcatedPromo') {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <BifurcatedPromo
                id={section.id}
                heading={section.heading || ''}
                title={section.title}
                description={section.description || ''}
                images={section.images || []}
              />
            </React.Fragment>
          );
        }

        // FEATURE GRID SECTION
        if (section.type === 'featureGrid' && section.features) {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <section
                className="mission-section feature-grid-section"
                id={section.id || `section-${index}`}
              >
                <h2 className="feature-grid-title">{section.title}</h2>
                <div className="feature-grid">
                  {section.features.map((feature, fIdx) => (
                    <div key={fIdx} className="feature-box">
                      <div className="feature-icon">{getIcon(feature.icon)}</div>
                      <h3 className="feature-title">{feature.title}</h3>
                      <p className="feature-description">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            </React.Fragment>
          );
        }

        // TESTIMONY SECTION
        if (section.type === 'testimony') {
          return (
            <React.Fragment key={index}>
              {renderHr && <hr className="section-divider" />}
              <section
                className="mission-section testimony-container"
                id={section.id || `section-${index}`}
              >
                <div className="testimony-content">
                  <div className="testimonial-card">
                    <p className="testimonial-text">"{section.quote}"</p>
                    <div className="testimonial-meta">
                      <div className="testimonial-name">{section.name}</div>
                      <div className="testimonial-designation">{section.designation}</div>
                    </div>
                  </div>
                </div>
              </section>
            </React.Fragment>
          );
        }

        return null;
      })}
    </div>
  );
}

export default CyberSecurity;
